import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { DocumentationService } from 'src/app/shared/services/documentation.service';
import { ToastService } from '@teorema/patron';
import { Button } from 'primeng/button';
import { Skeleton } from 'primeng/skeleton';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Documentation } from 'src/app/models/documentation.model';
import { DocumentationFormComponent } from 'src/app/modules/teo-wiki/documentations-list/form/documentation-form.component';

@Component({
  selector: 'app-ticket-documentations',
  standalone: true,
  imports: [
    CommonModule,
    Button,
    Skeleton,
    Tag,
    Tooltip,
    DocumentationFormComponent,
  ],
  templateUrl: './ticket-documentations.component.html',
  styleUrls: ['./ticket-documentations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketDocumentationsComponent implements OnInit {
  @Input() ticketId!: string;
  @Input() initialDocumentations: any[] = [];
  @Input() canWrite: boolean = true;
  @Output() documentationsUpdated = new EventEmitter<Documentation[]>();

  @ViewChild('documentationForm') documentationForm!: DocumentationFormComponent;

  documentations = signal<Documentation[]>([]);
  loading = signal(false);

  private docService = inject(DocumentationService);
  private toastService = inject(ToastService);

  readonly categories = [
    { label: 'Técnica', value: 'technical' },
    { label: 'Manual do Usuário', value: 'user_manual' },
    { label: 'Arquitetura', value: 'architecture' },
    { label: 'Processo', value: 'process' },
    { label: 'Integração', value: 'integration' },
  ];

  readonly visibilities = [
    { label: 'Pública', value: 'public' },
    { label: 'Privada', value: 'private' },
    { label: 'Interna', value: 'internal' },
  ];

  ngOnInit(): void {
    if (this.initialDocumentations?.length > 0) {
      this.documentations.set(this.initialDocumentations);
      this.documentationsUpdated.emit(this.documentations());
    } else if (this.ticketId) {
      this.loadDocumentations();
    }
  }

  async loadDocumentations(): Promise<void> {
    if (!this.ticketId) return;

    this.loading.set(true);
    try {
      const response = await this.docService.getDocumentationsByTicket(this.ticketId);
      const docs = response.data || response;
      const sorted = Array.isArray(docs)
        ? docs.sort((a: any, b: any) => {
            const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
            const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
            return dateB - dateA;
          })
        : [];
      this.documentations.set(sorted);
      this.documentationsUpdated.emit(this.documentations());
    } catch {
      this.toastService.show({ color: 'error', message: 'Erro ao carregar documentações' });
    } finally {
      this.loading.set(false);
    }
  }

  openNewDocumentation(): void {
    this.documentationForm.openModal(undefined, { id: this.ticketId });
  }

  editDocumentation(doc: Documentation): void {
    this.documentationForm.openModal(doc.id);
  }

  async deleteDocumentation(doc: Documentation): Promise<void> {
    if (!doc.id || !confirm('Tem certeza que deseja deletar esta documentação?')) return;

    this.loading.set(true);
    try {
      await this.docService.delete(doc.id);
      this.toastService.show({ color: 'success', message: 'Documentação deletada com sucesso!' });
      await this.loadDocumentations();
    } catch {
      this.toastService.show({ color: 'error', message: 'Erro ao deletar documentação' });
    } finally {
      this.loading.set(false);
    }
  }

  viewDocumentation(doc: Documentation): void {
    if (doc.slug) {
      window.open(`/teo-wiki/visualizar/${doc.slug}`, '_blank');
    }
  }

  onDocumentationSaved(): void {
    this.loadDocumentations();
  }

  getCategoryLabel(category: string): string {
    return this.categories.find(c => c.value === category)?.label || category;
  }

  getVisibilityLabel(visibility: string): string {
    return this.visibilities.find(v => v.value === visibility)?.label || visibility;
  }

  getCategorySeverity(
    category: string
  ): 'secondary' | 'success' | 'danger' | 'info' | 'contrast' | 'warn' {
    const map: Record<string, 'secondary' | 'success' | 'danger' | 'info' | 'contrast' | 'warn'> = {
      technical: 'info',
      user_manual: 'success',
      architecture: 'warn',
      process: 'secondary',
      integration: 'contrast',
    };
    return map[category] || 'info';
  }

  getVisibilitySeverity(
    visibility: string
  ): 'secondary' | 'success' | 'danger' | 'info' | 'contrast' | 'warn' {
    const map: Record<string, 'secondary' | 'success' | 'danger' | 'info' | 'contrast' | 'warn'> = {
      public: 'success',
      private: 'danger',
      internal: 'warn',
    };
    return map[visibility] || 'info';
  }
}
